import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Save, ArrowLeft, Settings, Image, Layers, BookOpen,
  MapPin, Megaphone, Star, Calculator, Sliders, Plus, Trash2,
  Check, X, ChevronDown, ChevronUp, GripVertical, Eye
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const SERIF = "'Cormorant Garamond', 'Playfair Display', Georgia, serif";

/* ═══ SHARED UI COMPONENTS ═══ */
const Input = ({ label, value, onChange, type = "text", placeholder, ...props }) => (
  <div>
    {label && <label className="block text-white/40 text-xs uppercase tracking-wider mb-1.5">{label}</label>}
    <input type={type} value={value || ""} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C9A96E]/50 focus:outline-none transition-colors"
      {...props} />
  </div>
);
const Textarea = ({ label, value, onChange, rows = 3, placeholder }) => (
  <div>
    {label && <label className="block text-white/40 text-xs uppercase tracking-wider mb-1.5">{label}</label>}
    <textarea value={value || ""} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C9A96E]/50 focus:outline-none transition-colors resize-none" />
  </div>
);
const Toggle = ({ label, checked, onChange, sub }) => (
  <div className="flex items-center justify-between py-2">
    <div>
      <p className="text-white/70 text-sm">{label}</p>
      {sub && <p className="text-white/30 text-[11px]">{sub}</p>}
    </div>
    <button onClick={() => onChange(!checked)}
      className={`w-10 h-5 rounded-full transition-colors ${checked ? "bg-[#C9A96E]" : "bg-white/10"}`}>
      <div className={`w-4 h-4 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  </div>
);
const SectionCard = ({ title, children, icon: Icon }) => (
  <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 mb-4">
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon className="w-4 h-4 text-[#C9A96E]" />}
      <h4 className="text-white/70 text-sm font-medium tracking-wider uppercase">{title}</h4>
    </div>
    {children}
  </div>
);

const TABS = [
  { id: "basic", label: "Basics", icon: Settings },
  { id: "hero", label: "Hero & Intro", icon: Image },
  { id: "foundation", label: "Foundation", icon: Layers },
  { id: "story", label: "Story", icon: BookOpen },
  { id: "onsite", label: "On-Site", icon: MapPin },
  { id: "distribution", label: "Distribution", icon: Megaphone },
  { id: "recommended", label: "Recommended", icon: Star },
  { id: "investment", label: "Investment", icon: Calculator },
  { id: "settings", label: "Settings", icon: Sliders },
];

export default function ProposalEditorV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isNew = id === "new";

  const [proposal, setProposal] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (isNew) {
      setProposal({
        hotel_name: "", location: "", contact_person: "", email: "",
        hero_images: [], personalized_message_en: "", personalized_message_de: "",
        intro_message_en: "", intro_message_de: "",
        status: "draft", access_type: "public",
        sections: null, // Will be stamped by backend from MASTER_SECTIONS_TEMPLATE
      });
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/proposals/${id}`, { headers });
        setProposal(data);
      } catch { toast.error("Could not load proposal"); navigate("/proposals"); }
      setLoading(false);
    })();
  }, [id]);

  const S = proposal?.sections || {};
  const setS = useCallback((path, value) => {
    setProposal(prev => {
      const next = { ...prev, sections: { ...prev.sections } };
      const parts = path.split(".");
      let obj = next.sections;
      for (let i = 0; i < parts.length - 1; i++) {
        obj[parts[i]] = { ...obj[parts[i]] };
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  }, []);

  const setField = useCallback((field, value) => {
    setProposal(prev => ({ ...prev, [field]: value }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await axios.post(`${API}/proposals`, proposal, { headers });
        toast.success("Proposal created");
        navigate(`/proposals/${data.id}/edit`);
      } else {
        await axios.put(`${API}/proposals/${id}`, proposal, { headers });
        toast.success("Saved");
      }
    } catch (e) { toast.error("Save failed"); }
    setSaving(false);
  };

  if (loading) return <div className="min-h-screen bg-[#030303] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#C9A96E]/30 border-t-[#C9A96E] rounded-full animate-spin" /></div>;
  if (!proposal) return null;

  return (
    <div className="min-h-screen bg-[#030303] text-white" data-testid="proposal-editor-v2">
      {/* HEADER */}
      <div className="sticky top-0 z-50 bg-[#030303]/95 backdrop-blur-md border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/proposals")} className="p-2 hover:bg-white/5 rounded-lg transition-colors">
              <ArrowLeft className="w-4 h-4 text-white/40" />
            </button>
            <div>
              <h1 className="text-white text-sm font-medium">{proposal.hotel_name || "New Proposal"}</h1>
              <p className="text-white/30 text-[11px]">{proposal.proposal_id || "Draft"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {proposal.proposal_id && (
              <a href={`/p/${proposal.proposal_id}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 text-white/40 text-xs hover:text-white/60 transition-colors">
                <Eye className="w-3.5 h-3.5" /> Preview
              </a>
            )}
            <button onClick={save} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#C9A96E] text-[#030303] rounded-lg text-sm font-medium hover:bg-[#D4B87A] transition-colors disabled:opacity-50"
              data-testid="save-btn">
              <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto pb-1">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-xs whitespace-nowrap transition-colors ${
                activeTab === tab.id ? "bg-white/[0.06] text-[#C9A96E]" : "text-white/30 hover:text-white/50"
              }`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-3.5 h-3.5" /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* TAB CONTENT */}
      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* TAB 1: BASICS */}
        {activeTab === "basic" && (
          <div className="space-y-4" data-testid="tab-content-basic">
            <SectionCard title="Property Information" icon={Settings}>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Hotel Name" value={proposal.hotel_name} onChange={v => setField("hotel_name", v)} />
                <Input label="Location" value={proposal.location} onChange={v => setField("location", v)} />
                <Input label="Contact Person" value={proposal.contact_person} onChange={v => setField("contact_person", v)} />
                <Input label="Email" value={proposal.email} onChange={v => setField("email", v)} />
              </div>
            </SectionCard>
            <SectionCard title="Status">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/40 text-xs uppercase tracking-wider mb-1.5">Status</label>
                  <select value={proposal.status || "draft"} onChange={e => setField("status", e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C9A96E]/50 focus:outline-none">
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white/40 text-xs uppercase tracking-wider mb-1.5">Access</label>
                  <select value={proposal.access_type || "public"} onChange={e => setField("access_type", e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C9A96E]/50 focus:outline-none">
                    <option value="public">Public</option>
                    <option value="password">Password Protected</option>
                  </select>
                </div>
              </div>
            </SectionCard>
          </div>
        )}

        {/* TAB 2: HERO & INTRO */}
        {activeTab === "hero" && (
          <div className="space-y-4" data-testid="tab-content-hero">
            <SectionCard title="Hero Images" icon={Image}>
              <div className="space-y-2 mb-3">
                {(proposal.hero_images || []).map((url, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={url} onChange={e => {
                      const imgs = [...(proposal.hero_images || [])];
                      imgs[i] = e.target.value;
                      setField("hero_images", imgs);
                    }} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" placeholder="Image URL" />
                    <button onClick={() => setField("hero_images", (proposal.hero_images || []).filter((_, j) => j !== i))}
                      className="p-2 text-red-400/50 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <button onClick={() => setField("hero_images", [...(proposal.hero_images || []), ""])}
                className="flex items-center gap-1.5 text-[#C9A96E] text-sm hover:text-[#D4B87A]">
                <Plus className="w-4 h-4" /> Add Image/Video URL
              </button>
            </SectionCard>
            <SectionCard title="Introduction Text">
              <div className="space-y-4">
                <Textarea label="Personalized Message (EN)" value={proposal.personalized_message_en} onChange={v => setField("personalized_message_en", v)} rows={4} />
                <Textarea label="Personalized Message (DE)" value={proposal.personalized_message_de} onChange={v => setField("personalized_message_de", v)} rows={4} />
                <Textarea label="Intro Message (EN)" value={proposal.intro_message_en} onChange={v => setField("intro_message_en", v)} rows={3} />
                <Textarea label="Intro Message (DE)" value={proposal.intro_message_de} onChange={v => setField("intro_message_de", v)} rows={3} />
              </div>
            </SectionCard>
          </div>
        )}

        {/* TAB 3: FOUNDATION */}
        {activeTab === "foundation" && S.foundation && (
          <div className="space-y-4" data-testid="tab-content-foundation">
            <SectionCard title="Membership Tiers" icon={Layers}>
              {(S.foundation.tiers || []).map((tier, i) => (
                <div key={tier.id} className="p-4 bg-white/[0.02] border border-white/[0.06] rounded-lg mb-3">
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <Input label="Tier Name" value={tier.name} onChange={v => {
                      const tiers = [...S.foundation.tiers]; tiers[i] = { ...tiers[i], name: v }; setS("foundation.tiers", tiers);
                    }} />
                    <Input label="Price (EUR)" type="number" value={tier.price} onChange={v => {
                      const tiers = [...S.foundation.tiers]; tiers[i] = { ...tiers[i], price: parseFloat(v) || 0 }; setS("foundation.tiers", tiers);
                    }} />
                    <div className="pt-5">
                      <Toggle label="Recommended" checked={tier.is_recommended} onChange={v => {
                        const tiers = S.foundation.tiers.map((t, j) => ({ ...t, is_recommended: j === i ? v : false }));
                        setS("foundation.tiers", tiers);
                      }} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input label="Description (EN)" value={tier.description_en} onChange={v => {
                      const tiers = [...S.foundation.tiers]; tiers[i] = { ...tiers[i], description_en: v }; setS("foundation.tiers", tiers);
                    }} />
                    <Input label="Description (DE)" value={tier.description_de} onChange={v => {
                      const tiers = [...S.foundation.tiers]; tiers[i] = { ...tiers[i], description_de: v }; setS("foundation.tiers", tiers);
                    }} />
                  </div>
                </div>
              ))}
            </SectionCard>
            <SectionCard title="Comparison Matrix">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 text-white/40 text-xs">Feature</th>
                      {(S.foundation.tiers || []).map(t => <th key={t.id} className="text-center py-2 text-white/40 text-xs w-24">{t.name}</th>)}
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(S.foundation.comparison_features || []).map((feat, fi) => (
                      <tr key={fi} className="border-b border-white/5">
                        <td className="py-2 pr-2">
                          <input value={feat.name_en || ""} onChange={e => {
                            const feats = [...S.foundation.comparison_features];
                            feats[fi] = { ...feats[fi], name_en: e.target.value };
                            setS("foundation.comparison_features", feats);
                          }} className="w-full bg-transparent text-white/70 text-sm focus:outline-none" />
                        </td>
                        {(S.foundation.tiers || []).map((_, ti) => (
                          <td key={ti} className="text-center py-2">
                            <button onClick={() => {
                              const feats = [...S.foundation.comparison_features];
                              feats[fi] = { ...feats[fi], [`tier_${ti + 1}`]: !feats[fi][`tier_${ti + 1}`] };
                              setS("foundation.comparison_features", feats);
                            }} className={`w-6 h-6 rounded-full inline-flex items-center justify-center ${feat[`tier_${ti + 1}`] ? "bg-[#C9A96E]/20 text-[#C9A96E]" : "bg-white/5 text-white/20"}`}>
                              {feat[`tier_${ti + 1}`] ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                            </button>
                          </td>
                        ))}
                        <td className="py-2">
                          <button onClick={() => {
                            const feats = S.foundation.comparison_features.filter((_, j) => j !== fi);
                            setS("foundation.comparison_features", feats);
                          }} className="text-red-400/30 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={() => {
                const feats = [...(S.foundation.comparison_features || []), { name_en: "", name_de: "", tier_1: false, tier_2: false, tier_3: false }];
                setS("foundation.comparison_features", feats);
              }} className="flex items-center gap-1.5 text-[#C9A96E] text-sm mt-3 hover:text-[#D4B87A]">
                <Plus className="w-4 h-4" /> Add Feature Row
              </button>
            </SectionCard>
          </div>
        )}

        {/* TAB 4: STORY ACTIVATION */}
        {activeTab === "story" && S.story_activation && (
          <div className="space-y-4" data-testid="tab-content-story">
            <Toggle label="Story Activation Enabled" checked={S.story_activation.enabled} onChange={v => setS("story_activation.enabled", v)} />
            <SectionCard title="Modules" icon={BookOpen}>
              {(S.story_activation.modules || []).map((mod, i) => (
                <ModuleEditor key={mod.id} mod={mod} onChange={updated => {
                  const mods = [...S.story_activation.modules]; mods[i] = updated; setS("story_activation.modules", mods);
                }} onDelete={() => {
                  setS("story_activation.modules", S.story_activation.modules.filter((_, j) => j !== i));
                }} />
              ))}
              <button onClick={() => {
                const mods = [...(S.story_activation.modules || []), {
                  id: `mod_${Date.now()}`, name_en: "", name_de: "", description_en: "", description_de: "",
                  price: 0, price_range_label: "", enabled: true, included_in_tiers: [], includes: []
                }];
                setS("story_activation.modules", mods);
              }} className="flex items-center gap-1.5 text-[#C9A96E] text-sm mt-3 hover:text-[#D4B87A]">
                <Plus className="w-4 h-4" /> Add Module
              </button>
            </SectionCard>
          </div>
        )}

        {/* TAB 5: ON-SITE */}
        {activeTab === "onsite" && S.onsite && (
          <div className="space-y-4" data-testid="tab-content-onsite">
            {/* VISIT */}
            <SectionCard title="Visit (Framework)" icon={MapPin}>
              <Toggle label="Visit Enabled" checked={S.onsite.visit?.enabled} onChange={v => setS("onsite.visit.enabled", v)} />
              <div className="grid grid-cols-2 gap-4 mt-3">
                <Input label="Team Size" type="number" value={S.onsite.visit?.team_size} onChange={v => setS("onsite.visit.team_size", parseInt(v) || 4)} />
                <Input label="Nights" type="number" value={S.onsite.visit?.nights} onChange={v => setS("onsite.visit.nights", parseInt(v) || 2)} />
              </div>
              <div className="mt-3">
                <label className="block text-white/40 text-xs uppercase tracking-wider mb-1.5">Travel Expenses</label>
                <select value={S.onsite.visit?.travel_expense_mode || "covered_by_property"} onChange={e => setS("onsite.visit.travel_expense_mode", e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C9A96E]/50 focus:outline-none">
                  <option value="covered_by_property">Covered by Property</option>
                  <option value="waived_if_on_route">Waived if on Route</option>
                </select>
              </div>
              <p className="text-white/20 text-[11px] mt-3 italic">Always hosted by the property. No price shown to the partner.</p>
            </SectionCard>

            {/* EVALUATION */}
            <SectionCard title="Evaluation (Optional)">
              <Toggle label="Evaluation Enabled" checked={S.onsite.evaluation?.enabled} onChange={v => setS("onsite.evaluation.enabled", v)} />
              {S.onsite.evaluation?.enabled && (S.onsite.evaluation.modules || []).map((mod, i) => (
                <ModuleEditor key={mod.id} mod={mod} onChange={updated => {
                  const mods = [...S.onsite.evaluation.modules]; mods[i] = updated; setS("onsite.evaluation.modules", mods);
                }} onDelete={() => {
                  setS("onsite.evaluation.modules", S.onsite.evaluation.modules.filter((_, j) => j !== i));
                }} />
              ))}
            </SectionCard>

            {/* PRODUCTION */}
            <SectionCard title="Production (Optional)">
              <Toggle label="Production Enabled" checked={S.onsite.production?.enabled} onChange={v => setS("onsite.production.enabled", v)} />
              {S.onsite.production?.enabled && (S.onsite.production.modules || []).map((mod, i) => (
                <ModuleEditor key={mod.id} mod={mod} onChange={updated => {
                  const mods = [...S.onsite.production.modules]; mods[i] = updated; setS("onsite.production.modules", mods);
                }} onDelete={() => {
                  setS("onsite.production.modules", S.onsite.production.modules.filter((_, j) => j !== i));
                }} />
              ))}
              {S.onsite.production?.enabled && (
                <button onClick={() => {
                  const mods = [...(S.onsite.production.modules || []), {
                    id: `prod_${Date.now()}`, name_en: "", name_de: "", description_en: "", description_de: "",
                    price: 0, price_range_label: "", enabled: false, includes: []
                  }];
                  setS("onsite.production.modules", mods);
                }} className="flex items-center gap-1.5 text-[#C9A96E] text-sm mt-3 hover:text-[#D4B87A]">
                  <Plus className="w-4 h-4" /> Add Production Module
                </button>
              )}
            </SectionCard>
          </div>
        )}

        {/* TAB 6: DISTRIBUTION */}
        {activeTab === "distribution" && S.distribution && (
          <div className="space-y-4" data-testid="tab-content-distribution">
            <Toggle label="Distribution Enabled" checked={S.distribution.enabled} onChange={v => setS("distribution.enabled", v)} />
            <SectionCard title="Channels" icon={Megaphone}>
              {(S.distribution.modules || []).map((mod, i) => (
                <div key={mod.id} className="p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg mb-3">
                  <div className="grid grid-cols-4 gap-3 items-center">
                    <Input value={mod.name_en} onChange={v => {
                      const mods = [...S.distribution.modules]; mods[i] = { ...mods[i], name_en: v }; setS("distribution.modules", mods);
                    }} />
                    <Input type="number" value={mod.price} onChange={v => {
                      const mods = [...S.distribution.modules]; mods[i] = { ...mods[i], price: parseFloat(v) || 0 }; setS("distribution.modules", mods);
                    }} placeholder="Price" />
                    <Toggle label="Enabled" checked={mod.enabled} onChange={v => {
                      const mods = [...S.distribution.modules]; mods[i] = { ...mods[i], enabled: v }; setS("distribution.modules", mods);
                    }} />
                    <Toggle label="Auto-included" checked={mod.included} onChange={v => {
                      const mods = [...S.distribution.modules]; mods[i] = { ...mods[i], included: v }; setS("distribution.modules", mods);
                    }} />
                  </div>
                  <div className="mt-2">
                    <p className="text-white/30 text-[11px] mb-1">Included in tiers:</p>
                    <div className="flex gap-2">
                      {(S.foundation?.tiers || []).map(tier => {
                        const incl = (mod.included_in_tiers || []).includes(tier.id);
                        return (
                          <button key={tier.id} onClick={() => {
                            const mods = [...S.distribution.modules];
                            const tiers = incl ? (mod.included_in_tiers || []).filter(t => t !== tier.id) : [...(mod.included_in_tiers || []), tier.id];
                            mods[i] = { ...mods[i], included_in_tiers: tiers };
                            setS("distribution.modules", mods);
                          }} className={`px-2 py-0.5 rounded text-[11px] ${incl ? "bg-[#C9A96E]/20 text-[#C9A96E]" : "bg-white/5 text-white/30"}`}>
                            {tier.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </SectionCard>
          </div>
        )}

        {/* TAB 7: RECOMMENDED SETUP */}
        {activeTab === "recommended" && S.recommended_setup && (
          <div className="space-y-4" data-testid="tab-content-recommended">
            <Toggle label="Recommended Setup Enabled" checked={S.recommended_setup.enabled} onChange={v => setS("recommended_setup.enabled", v)} />
            <SectionCard title="Configuration" icon={Star}>
              <div className="space-y-4">
                <Input label="Label (EN)" value={S.recommended_setup.label_en} onChange={v => setS("recommended_setup.label_en", v)} />
                <Input label="Label (DE)" value={S.recommended_setup.label_de} onChange={v => setS("recommended_setup.label_de", v)} />
                <Textarea label="Description (EN)" value={S.recommended_setup.description_en} onChange={v => setS("recommended_setup.description_en", v)} />
                <Textarea label="Description (DE)" value={S.recommended_setup.description_de} onChange={v => setS("recommended_setup.description_de", v)} />
                <div>
                  <label className="block text-white/40 text-xs uppercase tracking-wider mb-1.5">Recommended Tier</label>
                  <select value={S.recommended_setup.recommended_tier || ""} onChange={e => setS("recommended_setup.recommended_tier", e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C9A96E]/50 focus:outline-none">
                    {(S.foundation?.tiers || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Preselected Modules</p>
                  <div className="flex flex-wrap gap-2">
                    {[...(S.story_activation?.modules || []), ...(S.onsite?.evaluation?.modules || []), ...(S.onsite?.production?.modules || []), ...(S.distribution?.modules || []).filter(m => !m.included)].map(mod => {
                      const isSel = (S.recommended_setup.preselected_modules || []).includes(mod.id);
                      return (
                        <button key={mod.id} onClick={() => {
                          const sel = isSel ? (S.recommended_setup.preselected_modules || []).filter(m => m !== mod.id)
                            : [...(S.recommended_setup.preselected_modules || []), mod.id];
                          setS("recommended_setup.preselected_modules", sel);
                        }} className={`px-3 py-1.5 rounded-lg text-xs ${isSel ? "bg-[#C9A96E]/20 text-[#C9A96E]" : "bg-white/5 text-white/30 hover:bg-white/10"}`}>
                          {mod.name_en || mod.id}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>
        )}

        {/* TAB 8: INVESTMENT */}
        {activeTab === "investment" && S.investment && (
          <div className="space-y-4" data-testid="tab-content-investment">
            <SectionCard title="Investment Settings" icon={Calculator}>
              <Toggle label="Show Breakdown" checked={S.investment.show_breakdown} onChange={v => setS("investment.show_breakdown", v)} />
              <Toggle label="ROI Section Enabled" checked={S.investment.roi_enabled} onChange={v => setS("investment.roi_enabled", v)} />
            </SectionCard>
            {S.investment.roi_enabled && (
              <SectionCard title="ROI Scenarios">
                {(S.investment.roi_scenarios || []).map((scenario, i) => (
                  <div key={i} className="grid grid-cols-4 gap-3 mb-3">
                    <Input label="Label (EN)" value={scenario.label_en} onChange={v => {
                      const s = [...S.investment.roi_scenarios]; s[i] = { ...s[i], label_en: v }; setS("investment.roi_scenarios", s);
                    }} />
                    <Input label="Label (DE)" value={scenario.label_de} onChange={v => {
                      const s = [...S.investment.roi_scenarios]; s[i] = { ...s[i], label_de: v }; setS("investment.roi_scenarios", s);
                    }} />
                    <Input label="Bookings" type="number" value={scenario.bookings} onChange={v => {
                      const s = [...S.investment.roi_scenarios]; s[i] = { ...s[i], bookings: parseInt(v) || 0 }; setS("investment.roi_scenarios", s);
                    }} />
                    <Input label="Avg Value (EUR)" type="number" value={scenario.avg_value} onChange={v => {
                      const s = [...S.investment.roi_scenarios]; s[i] = { ...s[i], avg_value: parseInt(v) || 0 }; setS("investment.roi_scenarios", s);
                    }} />
                  </div>
                ))}
              </SectionCard>
            )}
          </div>
        )}

        {/* TAB 9: SETTINGS */}
        {activeTab === "settings" && S.settings && (
          <div className="space-y-4" data-testid="tab-content-settings">
            <SectionCard title="CTA Texts" icon={Sliders}>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Primary CTA (EN)" value={S.settings.cta_primary_en} onChange={v => setS("settings.cta_primary_en", v)} />
                <Input label="Primary CTA (DE)" value={S.settings.cta_primary_de} onChange={v => setS("settings.cta_primary_de", v)} />
                <Input label="Secondary CTA (EN)" value={S.settings.cta_secondary_en} onChange={v => setS("settings.cta_secondary_en", v)} />
                <Input label="Secondary CTA (DE)" value={S.settings.cta_secondary_de} onChange={v => setS("settings.cta_secondary_de", v)} />
                <Input label="Call CTA (EN)" value={S.settings.cta_call_en} onChange={v => setS("settings.cta_call_en", v)} />
                <Input label="Call CTA (DE)" value={S.settings.cta_call_de} onChange={v => setS("settings.cta_call_de", v)} />
              </div>
            </SectionCard>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══ MODULE EDITOR (reused across Story/Evaluation/Production) ═══ */
function ModuleEditor({ mod, onChange, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="p-4 bg-white/[0.02] border border-white/[0.06] rounded-lg mb-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3 flex-1">
          <button onClick={() => setExpanded(!expanded)} className="text-white/30 hover:text-white/50">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <input value={mod.name_en || ""} onChange={e => onChange({ ...mod, name_en: e.target.value })}
            className="bg-transparent text-white/80 text-sm font-medium focus:outline-none flex-1" placeholder="Module Name (EN)" />
        </div>
        <div className="flex items-center gap-3">
          <Toggle label="" checked={mod.enabled} onChange={v => onChange({ ...mod, enabled: v })} />
          <button onClick={onDelete} className="text-red-400/30 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {expanded && (
        <div className="space-y-3 mt-3 pl-7">
          <Input label="Name (DE)" value={mod.name_de} onChange={v => onChange({ ...mod, name_de: v })} />
          <Textarea label="Description (EN)" value={mod.description_en} onChange={v => onChange({ ...mod, description_en: v })} rows={2} />
          <Textarea label="Description (DE)" value={mod.description_de} onChange={v => onChange({ ...mod, description_de: v })} rows={2} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Price (EUR)" type="number" value={mod.price} onChange={v => onChange({ ...mod, price: parseFloat(v) || 0 })} />
            <Input label="Price Range Label" value={mod.price_range_label} onChange={v => onChange({ ...mod, price_range_label: v })} placeholder="e.g. 5,000 – 8,000" />
          </div>
          <div>
            <label className="block text-white/40 text-xs uppercase tracking-wider mb-1.5">Includes</label>
            {(mod.includes || []).map((item, i) => (
              <div key={i} className="flex items-center gap-2 mb-1">
                <input value={item} onChange={e => {
                  const inc = [...(mod.includes || [])]; inc[i] = e.target.value; onChange({ ...mod, includes: inc });
                }} className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-sm" />
                <button onClick={() => onChange({ ...mod, includes: (mod.includes || []).filter((_, j) => j !== i) })}
                  className="text-red-400/30 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
            <button onClick={() => onChange({ ...mod, includes: [...(mod.includes || []), ""] })}
              className="text-[#C9A96E] text-xs mt-1 hover:text-[#D4B87A]">+ Add Include</button>
          </div>
          {mod.note_en !== undefined && (
            <Input label="Note (EN)" value={mod.note_en} onChange={v => onChange({ ...mod, note_en: v })} />
          )}
        </div>
      )}
    </div>
  );
}
